import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import SymbolSearch from './SymbolSearch';
import { useAnalytics } from '../../hooks/useAnalytics';

interface HoldingFormData {
  symbol: string;
  exchange: string;
  quantity: string;
  average_price: string;
}

interface InlineHoldingFormProps {
  onSuccess: () => void;
  onCancel?: () => void; // New prop for cancel functionality
  initialRows?: number; // Number of initial rows (default: 2 for dashboard, 1 for holdings page)
  showPreFilledExample?: boolean; // Whether to show pre-filled example (default: true for dashboard, false for holdings)
  showUploadButton?: boolean; // Whether to show the Upload CSV button in zero holdings state (default: true for dashboard)
  showAddButton?: boolean; // Whether to show the "+ Add New" button (default: true for dashboard, false for holdings page)
}

const InlineHoldingForm: React.FC<InlineHoldingFormProps> = ({ 
  onSuccess, 
  onCancel,
  initialRows = 2, 
  showPreFilledExample = true,
  showUploadButton = true,
  showAddButton = true
}) => {
  const navigate = useNavigate();
  const { trackButtonClick, trackFormSubmission } = useAnalytics();
  // Create initial state based on props
  const createInitialState = (): HoldingFormData[] => {
    const rows: HoldingFormData[] = [];
    
    if (showPreFilledExample) {
      // Add pre-filled example row
      rows.push({
        symbol: 'RELIANCE',
        exchange: 'NSE',
        quantity: '100',
        average_price: '2500.00'
      });
    }
    
    // Add empty rows to reach initialRows count
    const emptyRowsNeeded = initialRows - rows.length;
    for (let i = 0; i < emptyRowsNeeded; i++) {
      rows.push({
        symbol: '',
        exchange: 'NSE',
        quantity: '',
        average_price: ''
      });
    }
    
    return rows;
  };

  const [holdings, setHoldings] = useState<HoldingFormData[]>(createInitialState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = React.useRef<HTMLDivElement>(null);

  const addNewRow = () => {
    setHoldings(prev => {
      const newHoldings = [...prev, {
        symbol: '',
        exchange: 'NSE',
        quantity: '',
        average_price: ''
      }];
      
      // Scroll to the new row after a brief delay
      setTimeout(() => {
        if (formRef.current) {
          const newRowIndex = newHoldings.length - 1;
          const newRow = formRef.current.querySelector(`[data-row-index="${newRowIndex}"]`);
          if (newRow) {
            newRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus on the symbol input of the new row
            const symbolInput = newRow.querySelector('input[placeholder="Symbol"]') as HTMLInputElement;
            if (symbolInput) {
              symbolInput.focus();
            }
          }
        }
      }, 100);
      
      return newHoldings;
    });
  };

  const removeRow = (index: number) => {
    // Prevent deletion of the first row if it's the only row
    if (holdings.length <= 1) {
      return;
    }
    
    // If showing pre-filled example, prevent deletion of the first row
    if (showPreFilledExample && index === 0) {
      return;
    }
    
    setHoldings(prev => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof HoldingFormData, value: string) => {
    setHoldings(prev => prev.map((holding, i) => 
      i === index ? { ...holding, [field]: value } : holding
    ));
  };

  const handleClearAll = () => {
    // Check if any holdings have filled fields
    const hasFilledRows = holdings.some(holding => 
      holding.symbol.trim() || holding.quantity.trim() || holding.average_price.trim()
    );
    
    if (hasFilledRows) {
      // Clear all fields and revert to one unfilled row
      const newHoldings = [];
      
      if (showPreFilledExample) {
        // Add pre-filled example row
        newHoldings.push({
          symbol: 'RELIANCE',
          exchange: 'NSE',
          quantity: '100',
          average_price: '2500.00'
        });
      }
      
      // Add one empty row
      newHoldings.push({
        symbol: '',
        exchange: 'NSE',
        quantity: '',
        average_price: ''
      });
      
      setHoldings(newHoldings);
    } else {
      // All rows are empty
      if (onCancel) {
        // If onCancel is provided (like in Holdings page), call it to dismiss the form
        onCancel();
      } else {
        // If onCancel is not provided (like in Dashboard), reset to initial state
        setHoldings(createInitialState());
      }
    }
    setError(null);
  };

  const validateHoldings = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    holdings.forEach((holding, index) => {
      // Skip validation for completely empty rows
      const isEmpty = !holding.symbol.trim() && !holding.quantity.trim() && !holding.average_price.trim();
      if (isEmpty) {
        return; // Skip empty rows
      }
      
      // For non-empty rows, validate all fields are present
      if (!holding.symbol.trim()) {
        errors.push(`Row ${index + 1}: Symbol is required`);
      }
      if (!holding.quantity.trim()) {
        errors.push(`Row ${index + 1}: Quantity is required`);
      }
      if (!holding.average_price.trim()) {
        errors.push(`Row ${index + 1}: Buy price is required`);
      }
      
      const quantity = parseFloat(holding.quantity);
      const averagePrice = parseFloat(holding.average_price);
      
      if (isNaN(quantity) || quantity <= 0) {
        errors.push(`Row ${index + 1}: Quantity must be a positive number`);
      }
      if (isNaN(averagePrice) || averagePrice <= 0) {
        errors.push(`Row ${index + 1}: Buy price must be a positive number`);
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const handleSubmit = async () => {
    const validation = validateHoldings();
    if (!validation.valid) {
      setError(validation.errors.join('\n'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      trackButtonClick('save_all_holdings', window.location.pathname);
      
      const holdingsToSubmit = holdings
        .filter(holding => 
          holding.symbol.trim() && holding.quantity.trim() && holding.average_price.trim()
        )
        .map(holding => ({
          symbol: holding.symbol.toUpperCase().trim(),
          exchange: holding.exchange,
          quantity: parseFloat(holding.quantity),
          average_price: parseFloat(holding.average_price)
        }));

      // Submit all holdings and collect results
      const results = await Promise.allSettled(
        holdingsToSubmit.map(holding => 
          apiService.holdings.addManual(holding)
        )
      );

      const successResults: any[] = [];
      const duplicateErrors: string[] = [];
      const otherErrors: string[] = [];
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          successResults.push(result.value);
        } else if (result.status === 'rejected' || !result.value.success) {
          const error = result.status === 'rejected' 
            ? result.reason?.response?.data?.error || result.reason?.message || 'Failed to add holding'
            : result.value.error || 'Failed to add holding';
          
          const symbol = holdingsToSubmit[index].symbol;
          const exchange = holdingsToSubmit[index].exchange;
          
          // Check if it's a duplicate holding error
          if (typeof error === 'string' && error.toLowerCase().includes('already exists')) {
            duplicateErrors.push(`${symbol} (${exchange}) already exists. You can edit the quantity or buy price if needed.`);
          } else {
            otherErrors.push(`${symbol} (${exchange}): ${error}`);
          }
        }
      });

      if (duplicateErrors.length > 0 || otherErrors.length > 0) {
        const allErrors = [];
        if (duplicateErrors.length > 0) {
          allErrors.push('Duplicate Holdings:');
          allErrors.push(...duplicateErrors);
        }
        if (otherErrors.length > 0) {
          if (duplicateErrors.length > 0) allErrors.push(''); // Empty line separator
          allErrors.push('Other Errors:');
          allErrors.push(...otherErrors);
        }
        setError(allErrors.join('\n'));
        
        // Track partial success
        if (successResults.length > 0) {
          trackFormSubmission('add_holdings', true);
          onSuccess();
        } else {
          trackFormSubmission('add_holdings', false, allErrors.join('\n'));
        }
      } else {
        trackFormSubmission('add_holdings', true);
        onSuccess();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to add holdings';
      trackFormSubmission('add_holdings', false, errorMessage);
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to add holdings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validHoldingsCount = holdings.filter(h => 
    h.symbol.trim() && h.quantity.trim() && h.average_price.trim()
  ).length;

  // For zero holdings state detection, exclude pre-filled examples when showPreFilledExample is true
  const userAddedHoldingsCount = holdings.filter((h, index) => {
    const isValid = h.symbol.trim() && h.quantity.trim() && h.average_price.trim();
    const isPreFilledExample = showPreFilledExample && index === 0 && h.symbol === 'RELIANCE';
    return isValid && !isPreFilledExample;
  }).length;

  // Check if any rows have data for Clear All button text
  const hasFilledRows = holdings.some(holding => 
    holding.symbol.trim() || holding.quantity.trim() || holding.average_price.trim()
  );


    return (
    <div className="space-y-4" ref={formRef}>
      {showAddButton && (
        <div className="flex items-center justify-end">
          <button
            onClick={addNewRow}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
          >
            + Add New
          </button>
        </div>
      )}

      <div className="space-y-4">
        {holdings.map((holding, index) => (
          <div key={index} className="grid grid-cols-12 gap-3 items-center p-3 bg-gray-50 rounded-lg md:grid-cols-12 grid-cols-1" data-row-index={index}>
            {/* Symbol */}
            <div className="col-span-12 md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1 md:hidden">Symbol</label>
              <SymbolSearch
                value={holding.symbol}
                onChange={(symbol) => updateRow(index, 'symbol', symbol)}
                exchange={holding.exchange as 'NSE' | 'BSE'}
                placeholder="Symbol"
                className="w-full"
                onSymbolSelect={(selectedSymbol) => {
                  updateRow(index, 'symbol', selectedSymbol.symbol);
                  updateRow(index, 'exchange', selectedSymbol.exchange);
                }}
              />
            </div>

            {/* Exchange */}
            <div className="col-span-12 md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1 md:hidden">Exchange</label>
              <select
                value={holding.exchange}
                onChange={(e) => updateRow(index, 'exchange', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
              </select>
            </div>

            {/* Quantity */}
            <div className="col-span-12 md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1 md:hidden">Quantity</label>
              <input
                type="number"
                value={holding.quantity}
                onChange={(e) => updateRow(index, 'quantity', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Qty"
                step="0.01"
                min="0"
              />
            </div>

            {/* Buy Price */}
            <div className="col-span-12 md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1 md:hidden">Buy Price</label>
              <input
                type="number"
                value={holding.average_price}
                onChange={(e) => updateRow(index, 'average_price', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Buy Price"
                step="0.01"
                min="0"
              />
            </div>

            {/* Remove Button - Only show for rows that can be deleted */}
            <div className="col-span-12 md:col-span-1 flex justify-center md:justify-start">
              {((showPreFilledExample && index > 0) || (!showPreFilledExample && holdings.length > 1)) && (
                <button
                  onClick={() => removeRow(index)}
                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                  title="Remove row"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 md:space-x-3 pt-4 border-t border-gray-200">
        <button
          onClick={handleClearAll}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 order-2 md:order-1"
        >
          {hasFilledRows ? 'Clear All' : 'Cancel'}
        </button>
        {showUploadButton && (showPreFilledExample ? userAddedHoldingsCount === 0 : true) && (
          <button
            onClick={() => {
              // Navigate to holdings page and trigger CSV upload modal
              navigate('/holdings?upload=csv');
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 order-1 md:order-2 flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload CSV
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || validHoldingsCount === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed order-3"
        >
          {isSubmitting ? 'Saving...' : `Save All (${validHoldingsCount})`}
        </button>
      </div>
    </div>
  );
};

export default InlineHoldingForm; 